/**
 * vqeEditAffordance — pure classification for the minimal edit affordance
 * (Workstream 2 of the VQE UX refinement; extended by Plan 9 §2.9/S13 for
 * DDL). Maps a statement's `RenderMode` (+ optional coarse `StatementKind`)
 * to the kind of edit surface the pencil affordance should open:
 *
 *   - `'compact'`  → SELECT + INSERT/UPDATE/DELETE/MERGE: open the anchored
 *                    Compact VQE popover.
 *   - `'ddl-card'` → the 7 true-DDL `StatementKind`s (create/alter/drop/
 *                    truncate/rename/grant/revoke): open the compact DDL
 *                    summary card (`compactDdlCard.ts`) instead of the
 *                    inline DDL toolbar.
 *   - `null`       → everything else: no pencil affordance.
 *
 * `RenderMode` alone can't drive the `'ddl-card'` target:
 * `detection/detectRenderMode.ts`'s `default:` arm folds EVERY unhandled
 * `StatementKind` — not just the 7 DDL kinds, but also `explain` /
 * `session-setting` / `transaction-control` / `call` / `pragma` /
 * `batch-separator` / `client-directive` — into `'unsupported-ddl'`. A
 * mode-only gate would put a dishonest "DDL card" pencil on an EXPLAIN or a
 * SET statement (§1.6: the pencil must lead somewhere real). So `'ddl-card'`
 * is additionally gated on the statement's coarse `StatementKind`, supplied
 * by the caller. Callers that omit `statementKind` (legacy call sites) get
 * the pre-Plan-9 behavior: DDL render modes resolve to `null`, same as
 * before.
 *
 * Procedural / parse-error / complex-set-precedence statements have no
 * structured compact editor and so get no pencil either.
 *
 * Kept as pure logic (no React, no enums — string-union per CLAUDE.md) so it's
 * unit-testable without standing up Monaco or a Resolver service.
 *
 * spec: docs/superpowers/specs/2026-06-14-vqe-ux-refinement-design.md § WS2;
 * Plan 9 §2.9/S13 for the `'ddl-card'` target.
 */

import type { StatementKind } from '../../../sql-grammar/model';
import type { RenderMode } from '../types';

export type VqeEditAffordanceTarget = 'compact' | 'ddl-card' | null;

/**
 * Why a statement whose edit target is `null` can't be edited visually — the
 * teaching signal behind the "never-silent" compact affordance (VQE Polish,
 * EDG-11..23). `null` means "genuinely nothing to teach" (empty line,
 * whitespace, a comment-only span, or a statement kind that is simply out of
 * this indicator's scope — EXPLAIN / SET / unknown) where staying silent is
 * correct.
 *
 *   - `'parse-error'`      → the statement didn't parse.
 *   - `'procedural'`       → a BEGIN…END / PL/SQL / DO block (runs as SQL).
 *   - `'set-op-precedence'`→ a mixed UNION/INTERSECT/EXCEPT precedence tree
 *                            VQE can't chart.
 *   - `'graph-table'`      → a SQL/PGQ `GRAPH_TABLE(…)` graph query (X1-T27
 *                            fix round 1, ITEM 2 — parity with
 *                            `'set-op-precedence'`: the inline pencil hides,
 *                            but this reason keeps the indicator non-silent).
 */
export type VqeUnavailableReason =
  | 'parse-error'
  | 'procedural'
  | 'set-op-precedence'
  | 'graph-table'
  | null;

/**
 * The four `RenderMode`s `detectRenderMode` already produces for the
 * unavailable statements this indicator teaches. Reuses `detectRenderMode`'s
 * existing signal wiring (its parse-error / `procedural-body` / set-op-
 * precedence / graph-table arms) rather than re-parsing — the mode IS the
 * reason.
 */
const UNAVAILABLE_REASON_BY_MODE: Partial<
  Record<RenderMode, Exclude<VqeUnavailableReason, null>>
> = {
  'parse-error': 'parse-error',
  'unsupported-procedural': 'procedural',
  'unsupported-set-precedence': 'set-op-precedence',
  'unsupported-graph-table': 'graph-table',
};

/**
 * Classify WHY a null-target statement can't be edited visually. Pure map over
 * the already-computed `RenderMode` — no re-parse. Every mode that carries a
 * teaching reason (`parse-error` / `unsupported-procedural` /
 * `unsupported-set-precedence` / `unsupported-graph-table`) also resolves to
 * a `null` edit target, so the reason never collides with an editable
 * statement; the remaining modes (SELECT/DML → editable;
 * `unsupported-ddl`/`unsupported-unknown` → out of scope) map to `null`.
 */
export function resolveUnavailableReason(mode: RenderMode | null): VqeUnavailableReason {
  if (!mode) return null;
  return UNAVAILABLE_REASON_BY_MODE[mode] ?? null;
}

/** Render modes that the Compact VQE popover can edit in v1. */
const COMPACT_MODES: ReadonlySet<RenderMode> = new Set<RenderMode>([
  'select',
  'insert',
  'update',
  'delete',
  'merge',
]);

/**
 * The 7 true-DDL `StatementKind`s. Re-declared here (not exported by
 * `detection/detectRenderMode.ts`) — MUST stay in sync with that module's
 * `DDL_KINDS` (`detectRenderMode.ts:21-29`). Deliberately narrower than
 * everything `detectRenderMode`'s `default:` arm maps to `'unsupported-ddl'`
 * — see the module docstring above.
 */
const DDL_CARD_KINDS: ReadonlySet<StatementKind> = new Set<StatementKind>([
  'ddl-create',
  'ddl-alter',
  'ddl-drop',
  'ddl-truncate',
  'ddl-rename',
  'grant',
  'revoke',
]);

export function resolveEditAffordanceTarget(
  mode: RenderMode | null,
  statementKind?: StatementKind | null,
): VqeEditAffordanceTarget {
  if (!mode) return null;
  if (COMPACT_MODES.has(mode)) return 'compact';
  if (statementKind && DDL_CARD_KINDS.has(statementKind)) return 'ddl-card';
  // Everything else — procedural / set-precedence / parse-error / the
  // render-mode-only DDL catch-all when no statementKind was supplied /
  // EXPLAIN-and-friends when a statementKind WAS supplied but isn't true
  // DDL — hides the pencil.
  return null;
}

/** Whether the affordance should render at all for this mode. */
export function shouldShowEditAffordance(
  mode: RenderMode | null,
  statementKind?: StatementKind | null,
): boolean {
  return resolveEditAffordanceTarget(mode, statementKind) !== null;
}
